import { Button } from '@repo/ui/src/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/src/components/ui/card';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChangePassword } from '../../../hooks/useProfile';
import { validatePasswordForm, type PasswordFormError } from '../../../lib/passwordForm';
import { SettingRow } from './SettingRow';

const ERROR_KEYS: Record<Exclude<PasswordFormError, null>, string> = {
  CURRENT_REQUIRED: 'profile.security.errCurrentRequired',
  TOO_SHORT: 'profile.security.errTooShort',
  MISMATCH: 'profile.security.errMismatch',
  SAME_AS_CURRENT: 'profile.security.errSameAsCurrent',
};

/**
 * Password card: read-by-default row that inline-edits in place rather than
 * showing a permanently-open form. Extracted out of PersonalCard (Task 3) so
 * profile and password live in their own settings sections.
 */
export function PasswordCard() {
  const { t } = useTranslation('settings');
  const changePassword = useChangePassword();

  // Password edit mode.
  const [editingPassword, setEditingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const resetPasswordForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
  };

  const closePasswordEditor = () => {
    resetPasswordForm();
    setEditingPassword(false);
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);

    const formError = validatePasswordForm({ currentPassword, newPassword, confirmPassword });
    if (formError) {
      setPasswordError(t(ERROR_KEYS[formError]));
      return;
    }

    changePassword.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          resetPasswordForm();
          setEditingPassword(false);
        },
        onError: (err: Error & { code?: string }) => {
          setPasswordError(
            err.code === 'INVALID_CURRENT_PASSWORD'
              ? t('profile.security.errWrongCurrent')
              : err.message,
          );
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('profile.password')}</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Password — read shows a masked placeholder, edit opens the form
            inline in the value column */}
        <SettingRow
          label={t('profile.password')}
          action={
            !editingPassword && (
              <Button variant="outline" size="sm" onClick={() => setEditingPassword(true)}>
                {t('profile.security.open')}
              </Button>
            )
          }
        >
          {editingPassword ? (
            <form onSubmit={handlePasswordSubmit} className="max-w-xs space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="current-password">{t('profile.security.currentPassword')}</Label>
                <Input
                  id="current-password"
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-password">{t('profile.security.newPassword')}</Label>
                <Input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <p className="text-xs text-surface-400">{t('profile.security.hint')}</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password">{t('profile.security.confirmPassword')}</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>

              {passwordError && <p className="text-xs text-red-600">{passwordError}</p>}

              <div className="flex gap-2">
                <Button type="submit" size="sm" disabled={changePassword.isPending}>
                  {changePassword.isPending
                    ? t('profile.security.submitting')
                    : t('profile.security.submit')}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={closePasswordEditor}>
                  {t('profile.cancel')}
                </Button>
              </div>
            </form>
          ) : (
            <p className="text-sm tracking-widest text-surface-700">••••••••</p>
          )}
        </SettingRow>
      </CardContent>
    </Card>
  );
}
